// ─────────────────────────────────────────────────────────────────────────────
// src/features/auth/AuthPage.tsx
//
// Page d'authentification — design Nawara Analytics
//   • Palette violet/rose identique à QueryBuilder
//   • Login + mot de passe avec show/hide (icône SVG professionnelle)
//   • Animation d'entrée du formulaire (floatUp)
//   • Animation de succès + slide-out vers l'app
//   • Chiffrement AES du mot de passe avant envoi (identique au backend)
//   • Flux "Mot de passe oublié" en 3 popups :
//       1️⃣  Saisie email → vérifie existence → envoie OTP
//       2️⃣  Saisie OTP  → timer 2 min, 3 tentatives max
//       3️⃣  Nouveau mot de passe + confirmation → chiffré AES → reset-password
//
//   🔧 URL du backend Avsoft (OTP) lue dynamiquement depuis :
//       GET /api/auth/config  →  { url_backend_bi: "http://...", nom_cube: "..." }
//       url_backend_bi  =  URL de base du backend Avsoft  (table parametre_bi)
//       Tous les appels OTP se font sur :  url_backend_bi + "/api/otp/..."
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useRef, useEffect, useCallback } from "react";

import { login } from "../features/auth/api";
import {
  cancelPasswordReset,
  deleteExpiredOtp,
  resetPassword,
  sendOtp,
  verifyOtp,
} from "../features/auth/otpApi";
import { ApiError, getErrorMessage } from "../shared/api/http";
import { session } from "../shared/api/session";
import { BRAND_NAME, LOGO_URL } from "../shared/ui/brand";
import { colors } from "../shared/ui/tokens";

// ── Palette Avantages Soft (AVDASH blue/teal) ─────────────────────────────────
const NW = {
  violet: colors.brand[600],
  violetDark: colors.brand[700],
  violetDeep: colors.brand[600],
  violetMed: colors.brand[100],
  violetLight: colors.brand[100],
  violetSoft: colors.neutral[100],
  rose: colors.accent[300],
  roseDark: colors.accent[500],
  roseMed: colors.accent[100],
  roseLight: colors.accent[50],
  white: colors.neutral[0],
  gray50: colors.neutral[50],
  gray100: colors.neutral[100],
  gray200: colors.neutral[200],
  gray300: colors.neutral[300],
  gray500: colors.neutral[500],
  gray700: colors.neutral[700],
  gray800: colors.neutral[800],
  success: colors.accent[500],
  successLight: colors.accent[100],
  danger: colors.danger[500],
  dangerLight: colors.danger[50],
  warning: colors.warning[500],
  warningLight: colors.warning[100],
};

const FONTS = {
  heading: "Inter, system-ui, -apple-system, sans-serif, sans-serif",
  body: "Inter, system-ui, -apple-system, sans-serif, sans-serif",
};

// ─────────────────────────────────────────────────────────────────────────────
// Icône SVG Œil — professionnelle, sans emoji
// ─────────────────────────────────────────────────────────────────────────────
const EyeIcon: React.FC<{ open: boolean; color?: string }> = ({
  open,
  color = NW.gray500,
}) =>
  open ? (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );

// ─────────────────────────────────────────────────────────────────────────────
// Spinner
// ─────────────────────────────────────────────────────────────────────────────
const NwSpinner: React.FC<{ size?: number; color?: string }> = ({
  size = 17,
  color = "#FFFFFF",
}) => (
  <>
    <style>
      {`@keyframes _nwSpin { to { transform: rotate(360deg); } }
      .nw-auth-spinner { border-radius:50%; animation: _nwSpin 0.65s linear infinite; flex-shrink:0; }`}
    </style>
    <span
      className="nw-auth-spinner"
      style={{
        width: size,
        height: size,
        border: `2.5px solid ${color}44`,
        borderTopColor: color,
        display: "inline-block",
      }}
    />
  </>
);

// ─────────────────────────────────────────────────────────────────────────────
// Alert inline
// ─────────────────────────────────────────────────────────────────────────────
type AlertKind = "error" | "success" | "warning" | "info";
const ALERT_CFG: Record<
  AlertKind,
  { bg: string; border: string; color: string; icon: string }
> = {
  error: {
    bg: NW.dangerLight,
    border: `${NW.danger}33`,
    color: NW.danger,
    icon: "⚠",
  },
  success: {
    bg: NW.successLight,
    border: `${NW.success}44`,
    color: NW.success,
    icon: "✓",
  },
  warning: {
    bg: NW.warningLight,
    border: `${NW.warning}44`,
    color: NW.warning,
    icon: "⚠",
  },
  info: {
    bg: NW.violetLight,
    border: `${NW.violet}33`,
    color: NW.violet,
    icon: "ℹ",
  },
};

const InlineAlert: React.FC<{ kind: AlertKind; msg: string }> = ({
  kind,
  msg,
}) => {
  const c = ALERT_CFG[kind];
  return (
    <div
      style={{
        padding: "9px 14px",
        background: c.bg,
        border: `1px solid ${c.border}`,
        borderRadius: 9,
        fontSize: 12.5,
        color: c.color,
        fontFamily: FONTS.body,
        display: "flex",
        alignItems: "center",
        gap: 7,
        animation: "nwFadeSlideUp 0.25s ease-out",
      }}
    >
      <span style={{ fontSize: 14, flexShrink: 0 }}>{c.icon}</span>
      {msg}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Bouton principal / ghost
// ─────────────────────────────────────────────────────────────────────────────
interface BtnProps {
  label: string;
  loadingLabel?: string;
  loading?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  type?: "submit" | "button";
  variant?: "primary" | "ghost";
}
const NwBtn: React.FC<BtnProps> = ({
  label,
  loadingLabel,
  loading,
  disabled,
  onClick,
  type = "button",
  variant = "primary",
}) => {
  const isPrimary = variant === "primary";
  return (
    <button
      type={type}
      disabled={disabled || loading}
      onClick={onClick}
      className="nw-btn-submit"
      style={{
        width: "100%",
        padding: "12px",
        marginTop: 4,
        background: isPrimary
          ? NW.violetDark
          : "transparent",
        backgroundSize: loading ? "200% 100%" : undefined,
        animation: loading ? "nwShimmer 1.4s linear infinite" : undefined,
        border: isPrimary ? "none" : `1.5px solid ${NW.gray200}`,
        borderRadius: 11,
        color: isPrimary ? "#FFFFFF" : NW.gray700,
        fontWeight: 700,
        fontSize: 13.5,
        fontFamily: FONTS.heading,
        cursor: disabled || loading ? "not-allowed" : "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        boxShadow: isPrimary ? "0 4px 18px rgba(24,75,140,0.28)" : "none",
        transition: "transform 0.15s, box-shadow 0.15s",
        opacity: disabled && !loading ? 0.55 : 1,
      }}
    >
      {loading ? (
        <>
          <NwSpinner size={15} color={isPrimary ? "#FFFFFF" : NW.violet} />
          {loadingLabel ?? label}
        </>
      ) : (
        label
      )}
    </button>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Champ Input réutilisable
// ─────────────────────────────────────────────────────────────────────────────
interface InputFieldProps {
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoComplete?: string;
  suffix?: React.ReactNode;
  disabled?: boolean;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  animDelay?: number;
}
const InputField: React.FC<InputFieldProps> = ({
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  autoComplete,
  suffix,
  disabled,
  inputRef,
  animDelay = 0,
}) => (
  <div className="nw-field" style={{ animationDelay: `${animDelay}s` }}>
    <label
      style={{
        display: "block",
        fontSize: 12,
        fontWeight: 600,
        color: NW.gray700,
        marginBottom: 5,
        fontFamily: FONTS.body,
      }}
    >
      {label}
    </label>
    <div style={{ position: "relative" }}>
      <input
        ref={inputRef}
        className="nw-input"
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        disabled={disabled}
        style={{
          width: "100%",
          padding: suffix ? "10px 44px 10px 13px" : "10px 13px",
          border: `1.5px solid ${NW.gray200}`,
          borderRadius: 10,
          fontSize: 13.5,
          fontFamily: FONTS.body,
          color: NW.gray800,
          background: disabled ? NW.gray100 : NW.gray50,
          boxSizing: "border-box",
          opacity: disabled ? 0.7 : 1,
        }}
      />
      {suffix && (
        <div
          style={{
            position: "absolute",
            right: 12,
            top: "50%",
            transform: "translateY(-50%)",
            display: "flex",
            alignItems: "center",
          }}
        >
          {suffix}
        </div>
      )}
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Popup générique
// ─────────────────────────────────────────────────────────────────────────────
interface ModalProps {
  visible: boolean;
  onClose?: () => void;
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}
const Modal: React.FC<ModalProps> = ({
  visible,
  onClose,
  title,
  subtitle,
  icon,
  children,
}) => {
  if (!visible) return null;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(15,23,42,0.72)",
        backdropFilter: "blur(6px)",
        animation: "nwBgFadeIn 0.25s ease-out",
        padding: 16,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && onClose) onClose();
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 420,
          background: NW.white,
          borderRadius: 20,
          padding: "36px 32px 28px",
          boxShadow:
            "0 0 0 1px rgba(24,75,140,0.1), 0 32px 90px rgba(15,23,42,0.4)",
          animation: "nwAuthFloatUp 0.4s cubic-bezier(0.22,1,0.36,1) both",
          position: "relative",
        }}
      >
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            style={{
              position: "absolute",
              top: 14,
              right: 16,
              background: "none",
              border: "none",
              cursor: "pointer",
              color: NW.gray500,
              fontSize: 20,
              lineHeight: 1,
              padding: 4,
              borderRadius: 6,
              transition: "color 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = NW.violet)}
            onMouseLeave={(e) => (e.currentTarget.style.color = NW.gray300)}
          >
            ×
          </button>
        )}
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          {icon && (
            <div
              style={{
                marginBottom: 12,
                display: "flex",
                justifyContent: "center",
              }}
            >
              {icon}
            </div>
          )}
          <h2
            style={{
              margin: 0,
              fontSize: 19,
              fontWeight: 800,
              fontFamily: FONTS.heading,
              color: NW.gray800,
              letterSpacing: -0.3,
            }}
          >
            {title}
          </h2>
          {subtitle && (
            <p
              style={{
                margin: "6px 0 0",
                fontSize: 12.5,
                color: NW.gray500,
                fontFamily: FONTS.body,
                lineHeight: 1.5,
              }}
            >
              {subtitle}
            </p>
          )}
        </div>
        {children}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Cercle icône coloré
// ─────────────────────────────────────────────────────────────────────────────
const IconCircle: React.FC<{
  gradient: [string, string];
  glow: string;
  children: React.ReactNode;
}> = ({ gradient, glow, children }) => (
  <div
    style={{
      width: 52,
      height: 52,
      borderRadius: "50%",
      background: `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: `0 0 20px ${glow}`,
    }}
  >
    {children}
  </div>
);

const MailSvg = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#FFFFFF"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);
const LockSvg = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#FFFFFF"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="5" y="11" width="14" height="10" rx="2" ry="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);
const KeySvg = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#FFFFFF"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
  </svg>
);

// ─────────────────────────────────────────────────────────────────────────────
// 1️⃣  POPUP EMAIL
//     → GET  /api/auth/config          pour résoudre url_backend_bi
//     → POST url_backend_bi/api/otp/send-otp
// ─────────────────────────────────────────────────────────────────────────────
interface PopupEmailProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: (email: string) => void;
}
const PopupEmail: React.FC<PopupEmailProps> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const emailRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (visible) {
      setEmail("");
      setError("");
      setLoading(false);
    }
    if (visible) setTimeout(() => emailRef.current?.focus(), 100);
  }, [visible]);

  const handleSubmit = async () => {
    if (!email.trim()) {
      setError("Veuillez saisir votre adresse e-mail.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Format d'e-mail invalide.");
      return;
    }

    setError("");
    setLoading(true);
    try {
      const res = await sendOtp(email.trim());
      if (!res.success) {
        setError(res.message || "Adresse e-mail introuvable ou erreur serveur.");
        setLoading(false);
        return;
      }
      setLoading(false);
      onSuccess(email.trim());
    } catch (err) {
      setError(getErrorMessage(err, "Adresse e-mail introuvable ou erreur serveur."));
      setLoading(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onClose={onClose}
      title="Mot de passe oublié"
      subtitle="Saisissez votre adresse e-mail. Nous vous enverrons un code de vérification à 6 chiffres."
      icon={
        <IconCircle
          gradient={[NW.violetDark, NW.violet]}
          glow="rgba(29,91,168,0.3)"
        >
          <MailSvg />
        </IconCircle>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <InputField
          label="Adresse e-mail"
          type="email"
          value={email}
          onChange={setEmail}
          placeholder="exemple@domaine.com"
          autoComplete="email"
          inputRef={emailRef}
        />
        {error && <InlineAlert kind="error" msg={error} />}
        <NwBtn
          label="Envoyer le code OTP"
          loadingLabel="Envoi en cours…"
          loading={loading}
          onClick={handleSubmit}
        />
        <NwBtn label="Annuler" variant="ghost" onClick={onClose} />
      </div>
    </Modal>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// 2️⃣  POPUP OTP — timer 2 min, 3 tentatives
//     → POST url_backend_bi/api/otp/verify-otp
//     → POST url_backend_bi/api/otp/delete-expired-otp  (expiry / cancel)
//     → POST url_backend_bi/api/otp/send-otp            (renvoi)
// ─────────────────────────────────────────────────────────────────────────────
const OTP_DURATION = 120; // secondes
const OTP_MAX_TRIES = 3;

interface PopupOtpProps {
  visible: boolean;
  email: string;
  onClose: () => void;
  onSuccess: () => void;
}
const PopupOtp: React.FC<PopupOtpProps> = ({
  visible,
  email,
  onClose,
  onSuccess,
}) => {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tries, setTries] = useState(0);
  const [seconds, setSeconds] = useState(OTP_DURATION);
  const [expired, setExpired] = useState(false);
  const [resending, setResending] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const deleteExpired = useCallback(async () => {
    try {
      await deleteExpiredOtp(email);
    } catch {
      /* silencieux */
    }
  }, [email]);

  const startTimer = useCallback(() => {
    setSeconds(OTP_DURATION);
    setExpired(false);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current!);
          setExpired(true);
          deleteExpired();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [deleteExpired]);

  useEffect(() => {
    if (visible) {
      setCode("");
      setError("");
      setTries(0);
      startTimer();
      setTimeout(() => inputRef.current?.focus(), 100);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [visible, startTimer]);

  const fmt = (s: number) =>
    `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  const blocked = expired || tries >= OTP_MAX_TRIES;
  const timerColor = expired
    ? NW.danger
    : seconds <= 30
      ? NW.warning
      : NW.success;
  const pct = (seconds / OTP_DURATION) * 100;

  const handleVerify = async () => {
    if (!code.trim()) {
      setError("Veuillez saisir le code OTP.");
      return;
    }
    if (expired) {
      setError("Le code a expiré. Demandez un nouveau code.");
      return;
    }

    setError("");
    setLoading(true);
    try {
      // Erreur HTTP (code refusé) = même traitement qu'un success:false ;
      // seule une erreur réseau est remontée au catch.
      const json = await verifyOtp(email, code.trim()).catch((err) => {
        if (err instanceof ApiError && !err.isNetworkError) {
          return { success: false };
        }
        throw err;
      });

      if (!json.success) {
        const newTries = tries + 1;
        setTries(newTries);
        if (newTries >= OTP_MAX_TRIES) {
          setError(
            `Code incorrect — ${OTP_MAX_TRIES} tentatives épuisées. Demandez un nouveau code.`,
          );
          setExpired(true);
          if (timerRef.current) clearInterval(timerRef.current);
        } else {
          setError(`Code incorrect — tentative ${newTries}/${OTP_MAX_TRIES}.`);
        }
        setLoading(false);
        return;
      }

      if (timerRef.current) clearInterval(timerRef.current);
      setLoading(false);
      onSuccess();
    } catch {
      setError("Serveur inaccessible.");
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setError("");
    try {
      const json = await sendOtp(email);
      if (!json.success) {
        setError(json.message || "Erreur lors du renvoi.");
      } else {
        setCode("");
        setTries(0);
        startTimer();
      }
    } catch (err) {
      setError(getErrorMessage(err, "Erreur lors du renvoi."));
    }
    setResending(false);
  };

  const handleClose = async () => {
    if (timerRef.current) clearInterval(timerRef.current);
    await deleteExpired();
    onClose();
  };

  return (
    <Modal
      visible={visible}
      onClose={handleClose}
      title="Vérification OTP"
      subtitle={`Code envoyé à ${email}`}
      icon={
        <IconCircle gradient={[NW.violet, NW.rose]} glow="rgba(127,200,189,0.3)">
          <LockSvg />
        </IconCircle>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {/* Timer + barre + tentatives */}
        <div
          style={{
            background: NW.gray50,
            border: `1.5px solid ${timerColor}44`,
            borderRadius: 12,
            padding: "12px 16px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 8,
            }}
          >
            <span
              style={{
                fontSize: 12,
                color: NW.gray500,
                fontFamily: FONTS.body,
              }}
            >
              {expired ? "Code expiré" : "Temps restant"}
            </span>
            <span
              style={{
                fontSize: 22,
                fontWeight: 800,
                color: timerColor,
                fontFamily: "monospace",
                letterSpacing: 1,
              }}
            >
              {fmt(seconds)}
            </span>
          </div>
          <div
            style={{
              height: 4,
              background: NW.gray200,
              borderRadius: 99,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${pct}%`,
                borderRadius: 99,
                background: expired
                  ? NW.danger
                  : pct < 25
                    ? NW.warning
                    : NW.violet,
                transition: "width 1s linear, background 0.5s",
              }}
            />
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginTop: 8,
            }}
          >
            {Array.from({ length: OTP_MAX_TRIES }).map((_, i) => (
              <div
                key={i}
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: i < tries ? NW.danger : NW.gray300,
                  transition: "background 0.3s",
                }}
              />
            ))}
            <span
              style={{
                fontSize: 11,
                color: NW.gray500,
                marginLeft: 4,
                fontFamily: FONTS.body,
              }}
            >
              {OTP_MAX_TRIES - tries} tentative
              {OTP_MAX_TRIES - tries > 1 ? "s" : ""} restante
              {OTP_MAX_TRIES - tries > 1 ? "s" : ""}
            </span>
          </div>
        </div>

        <InputField
          label="Code OTP à 6 chiffres"
          value={code}
          onChange={(v) => setCode(v.replace(/\D/g, "").slice(0, 6))}
          placeholder="000000"
          inputRef={inputRef}
          disabled={blocked}
        />

        {error && <InlineAlert kind="error" msg={error} />}

        <NwBtn
          label="Valider le code"
          loadingLabel="Vérification…"
          loading={loading}
          disabled={blocked}
          onClick={handleVerify}
        />

        <div style={{ textAlign: "center" }}>
          <button
            type="button"
            onClick={handleResend}
            disabled={resending || loading}
            style={{
              background: "none",
              border: "none",
              cursor: resending || loading ? "not-allowed" : "pointer",
              fontSize: 12.5,
              color: NW.violet,
              fontFamily: FONTS.body,
              textDecoration: "underline",
              opacity: resending ? 0.6 : 1,
            }}
          >
            {resending ? "Renvoi en cours…" : "Renvoyer un nouveau code"}
          </button>
        </div>

        <NwBtn label="Annuler" variant="ghost" onClick={handleClose} />
      </div>
    </Modal>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// 3️⃣  POPUP NOUVEAU MOT DE PASSE
//     → POST url_backend_bi/api/otp/reset-password          (NewPassword chiffré AES)
//     → POST url_backend_bi/api/otp/cancel-password-reset   (si annulation)
// ─────────────────────────────────────────────────────────────────────────────
interface PopupResetPasswordProps {
  visible: boolean;
  email: string;
  onClose: () => void;
  onSuccess: () => void;
}
const PopupResetPassword: React.FC<PopupResetPasswordProps> = ({
  visible,
  email,
  onClose,
  onSuccess,
}) => {
  const [newPwd, setNewPwd] = useState("");
  const [confPwd, setConfPwd] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConf, setShowConf] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (visible) {
      setNewPwd("");
      setConfPwd("");
      setError("");
      setDone(false);
    }
  }, [visible]);

  const strength = (() => {
    if (!newPwd) return 0;
    let s = 0;
    if (newPwd.length >= 8) s++;
    if (/[A-Z]/.test(newPwd)) s++;
    if (/[0-9]/.test(newPwd)) s++;
    if (/[^A-Za-z0-9]/.test(newPwd)) s++;
    return s;
  })();
  const STRENGTH_LABELS = ["", "Faible", "Correct", "Bon", "Fort"];
  const STRENGTH_COLORS = ["", NW.danger, NW.warning, "#4A86C7", NW.success];

  const handleReset = async () => {
    if (!newPwd) {
      setError("Veuillez saisir le nouveau mot de passe.");
      return;
    }
    if (newPwd.length < 6) {
      setError("Le mot de passe doit contenir au moins 6 caractères.");
      return;
    }
    if (newPwd !== confPwd) {
      setError("Les deux mots de passe ne correspondent pas.");
      return;
    }

    setError("");
    setLoading(true);
    // ❌ Supprimé : ne pas chiffrer ici — le backend (reset-password)
    // appelle déjà _encryptionService.Encrypt(newPassword) lui-même.
    // Si on chiffre ici aussi → double chiffrement → login impossible.

    try {
      const json = await resetPassword(email, newPwd);

      if (!json.success) {
        setError(json.message || "Erreur lors de la réinitialisation.");
        setLoading(false);
        return;
      }

      setLoading(false);
      setDone(true);
      setTimeout(() => onSuccess(), 1800);
    } catch (err) {
      setError(getErrorMessage(err, "Erreur lors de la réinitialisation."));
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    try {
      await cancelPasswordReset(email);
    } catch {
      /* silencieux */
    }
    onClose();
  };

  const EyeBtn = (open: boolean, toggle: () => void) => (
    <button
      type="button"
      onClick={toggle}
      tabIndex={-1}
      style={{
        background: "none",
        border: "none",
        cursor: "pointer",
        padding: 0,
        display: "flex",
        alignItems: "center",
      }}
    >
      <EyeIcon open={open} />
    </button>
  );

  return (
    <Modal
      visible={visible}
      onClose={handleCancel}
      title="Nouveau mot de passe"
      subtitle="Choisissez un mot de passe sécurisé pour votre compte."
      icon={
        <IconCircle
          gradient={[NW.violetDark, NW.rose]}
          glow="rgba(29,91,168,0.3)"
        >
          <KeySvg />
        </IconCircle>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {done ? (
          <InlineAlert
            kind="success"
            msg="Mot de passe réinitialisé avec succès ! Redirection vers la connexion…"
          />
        ) : (
          <>
            <InputField
              label="Nouveau mot de passe"
              type={showNew ? "text" : "password"}
              value={newPwd}
              onChange={setNewPwd}
              placeholder="••••••••"
              autoComplete="new-password"
              suffix={EyeBtn(showNew, () => setShowNew((v) => !v))}
            />

            {newPwd.length > 0 && (
              <div>
                <div style={{ display: "flex", gap: 4, marginBottom: 4 }}>
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      style={{
                        flex: 1,
                        height: 3,
                        borderRadius: 99,
                        background:
                          i <= strength
                            ? STRENGTH_COLORS[strength]
                            : NW.gray200,
                        transition: "background 0.3s",
                      }}
                    />
                  ))}
                </div>
                <span
                  style={{
                    fontSize: 11,
                    color: STRENGTH_COLORS[strength],
                    fontFamily: FONTS.body,
                  }}
                >
                  Force : {STRENGTH_LABELS[strength]}
                </span>
              </div>
            )}

            <InputField
              label="Confirmer le mot de passe"
              type={showConf ? "text" : "password"}
              value={confPwd}
              onChange={setConfPwd}
              placeholder="••••••••"
              autoComplete="new-password"
              suffix={EyeBtn(showConf, () => setShowConf((v) => !v))}
            />

            {confPwd.length > 0 && (
              <span
                style={{
                  fontSize: 11,
                  fontFamily: FONTS.body,
                  color: newPwd === confPwd ? NW.success : NW.danger,
                }}
              >
                {newPwd === confPwd
                  ? "✓ Les mots de passe correspondent"
                  : "✗ Ne correspondent pas"}
              </span>
            )}

            {error && <InlineAlert kind="error" msg={error} />}

            <NwBtn
              label="Réinitialiser le mot de passe"
              loadingLabel="Réinitialisation…"
              loading={loading}
              onClick={handleReset}
            />
            <NwBtn label="Annuler" variant="ghost" onClick={handleCancel} />
          </>
        )}
      </div>
    </Modal>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Flux "mot de passe oublié"
// ─────────────────────────────────────────────────────────────────────────────
type ForgotStep = "none" | "email" | "otp" | "reset";

// ─────────────────────────────────────────────────────────────────────────────
// Composant principal AuthPage
// ─────────────────────────────────────────────────────────────────────────────
interface AuthPageProps {
  onLogin?: () => void;
}

const AuthPage: React.FC<AuthPageProps> = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [phase, setPhase] = useState<"idle" | "success" | "leaving">("idle");

  const [forgotStep, setForgotStep] = useState<ForgotStep>("none");
  const [forgotEmail, setForgotEmail] = useState("");

  const usernameRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    usernameRef.current?.focus();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim()) {
      setError("Veuillez saisir votre identifiant.");
      return;
    }
    if (!password.trim()) {
      setError("Veuillez saisir votre mot de passe.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const { token, user } = await login({
        Identifier: username,
        Password: password,
      });
      if (token) session.save(token, user ?? undefined);

      // ✅ rôle/dépôt ne sont pas stockés en localStorage (modifiable côté client → faille).
      // Ils sont revérifiés via GET /auth/verify (useUserScope).

      setLoading(false);
      setPhase("success");
      setTimeout(() => {
        setPhase("leaving");
        setTimeout(() => {
          if (onLogin) onLogin();
        }, 500);
      }, 1400);
    } catch (err) {
      setError(getErrorMessage(err, "Identifiants incorrects."));
      setLoading(false);
    }
  };

  const cardAnim =
    phase === "leaving"
      ? "nwAuthSlideOut 0.5s cubic-bezier(0.4,0,1,1) forwards"
      : "nwAuthFloatUp 0.55s cubic-bezier(0.22,1,0.36,1) both";

  const bgAnim =
    phase === "leaving"
      ? "nwBgFadeOut 0.5s ease-in forwards"
      : "nwBgFadeIn 0.6s ease-out both";

  return (
    <>
      <style>{`
        @keyframes nwAuthFloatUp {
          from { opacity:0; transform:translateY(36px) scale(0.97); }
          to   { opacity:1; transform:translateY(0) scale(1); }
        }
        @keyframes nwAuthSlideOut {
          from { opacity:1; transform:translateY(0) scale(1); }
          to   { opacity:0; transform:translateY(-44px) scale(0.96); }
        }
        @keyframes nwBgFadeIn  { from{opacity:0} to{opacity:1} }
        @keyframes nwBgFadeOut { from{opacity:1} to{opacity:0} }
        @keyframes nwSuccessPop {
          0%  {transform:scale(0.85);opacity:0}
          60% {transform:scale(1.06)}
          100%{transform:scale(1);opacity:1}
        }
        @keyframes nwOrbit {
          0%  {transform:rotate(0deg)   translateX(28px) rotate(0deg)}
          100%{transform:rotate(360deg) translateX(28px) rotate(-360deg)}
        }
        @keyframes nwShimmer {
          0%  {background-position:-200% center}
          100%{background-position: 200% center}
        }
        @keyframes nwFadeSlideUp {
          from{opacity:0;transform:translateY(10px)}
          to  {opacity:1;transform:translateY(0)}
        }
        .nw-input { transition:border-color .15s,box-shadow .15s; }
        .nw-input:focus {
          border-color:${NW.violet} !important;
          box-shadow:0 0 0 3px ${NW.violetMed} !important;
          outline:none;
        }
        .nw-btn-submit:hover:not(:disabled){
          transform:translateY(-2px);
          box-shadow:0 8px 28px rgba(29,91,168,0.42) !important;
        }
        .nw-btn-submit:active:not(:disabled){transform:translateY(0)}
        .nw-btn-submit:disabled{cursor:not-allowed}
        .nw-field{animation:nwFadeSlideUp .4s both}
        .nw-field:nth-child(1){animation-delay:.12s}
        .nw-field:nth-child(2){animation-delay:.22s}
        .nw-field:nth-child(3){animation-delay:.32s}
      `}</style>

      {/* Fond */}
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#1D5BA8",
          padding: 20,
          position: "relative",
          overflow: "hidden",
          animation: bgAnim,
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "radial-gradient(circle,rgba(255,255,255,0.045) 1px,transparent 1px)",
            backgroundSize: "32px 32px",
            pointerEvents: "none",
          }}
        />

        {/* Carte */}
        <div
          style={{
            width: "100%",
            maxWidth: 420,
            background: NW.white,
            borderRadius: 20,
            padding: "40px 36px 32px",
            boxShadow:
              "0 0 0 1px rgba(29,91,168,0.08),0 24px 80px rgba(15,23,42,0.35)",
            animation: cardAnim,
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Logo */}
          <div style={{ textAlign: "center", marginBottom: 28 }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 12,
              }}
            >
              <img
                src={LOGO_URL}
                alt={BRAND_NAME}
                style={{
                  width: 88,
                  height: 88,
                  objectFit: "contain",
                }}
              />
            </div>
            <h1
              style={{
                margin: 0,
                fontSize: 22,
                fontWeight: 800,
                fontFamily: FONTS.heading,
                color: NW.violetDark,
                letterSpacing: -0.3,
              }}
            >
              AvDash
            </h1>
            <p
              style={{
                margin: "5px 0 0",
                fontSize: 12.5,
                color: NW.gray500,
                fontFamily: FONTS.body,
              }}
            >
              Connectez-vous pour accéder à votre espace BI
            </p>
          </div>

          {/* Formulaire */}
          <form
            onSubmit={handleSubmit}
            style={{ display: "flex", flexDirection: "column", gap: 14 }}
          >
            <InputField
              label="Identifiant"
              type="text"
              value={username}
              onChange={setUsername}
              placeholder="Votre identifiant ou e-mail"
              autoComplete="username"
              inputRef={usernameRef}
              animDelay={0.12}
            />

            {/* Mot de passe + lien oublié sous le label */}
            <div className="nw-field" style={{ animationDelay: "0.22s" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 5,
                }}
              >
                <label
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: NW.gray700,
                    fontFamily: FONTS.body,
                  }}
                >
                  Mot de passe
                </label>
                <button
                  type="button"
                  onClick={() => setForgotStep("email")}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                    fontSize: 11.5,
                    fontWeight: 600,
                    color: NW.violet,
                    fontFamily: FONTS.body,
                    textDecoration: "underline",
                  }}
                >
                  Mot de passe oublié ?
                </button>
              </div>
              <div style={{ position: "relative" }}>
                <input
                  className="nw-input"
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  style={{
                    width: "100%",
                    padding: "10px 44px 10px 13px",
                    border: `1.5px solid ${NW.gray200}`,
                    borderRadius: 10,
                    fontSize: 13.5,
                    fontFamily: FONTS.body,
                    color: NW.gray800,
                    background: NW.gray50,
                    boxSizing: "border-box",
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  tabIndex={-1}
                  aria-label={
                    showPwd
                      ? "Masquer le mot de passe"
                      : "Afficher le mot de passe"
                  }
                  style={{
                    position: "absolute",
                    right: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    color: NW.gray500,
                    transition: "color .15s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.color = NW.violet)
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.color = NW.gray500)
                  }
                >
                  <EyeIcon open={showPwd} />
                </button>
              </div>
            </div>

            {error && <InlineAlert kind="error" msg={error} />}

            {phase === "success" && (
              <div
                style={{
                  padding: "11px 16px",
                  background: NW.successLight,
                  border: `1.5px solid ${NW.success}66`,
                  borderRadius: 11,
                  fontSize: 13,
                  color: NW.success,
                  fontFamily: FONTS.body,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: 9,
                  animation: "nwSuccessPop 0.4s ease-out",
                }}
              >
                <span
                  style={{
                    fontSize: 20,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 28,
                    height: 28,
                    background: NW.success,
                    borderRadius: "50%",
                    flexShrink: 0,
                  }}
                >
                  <span
                    style={{ filter: "brightness(0) invert(1)", fontSize: 13 }}
                  >
                    ✓
                  </span>
                </span>
                <span>
                  Connexion réussie !{" "}
                  <span style={{ fontWeight: 400, color: "#00806F" }}>
                    Redirection en cours…
                  </span>
                </span>
              </div>
            )}

            <div className="nw-field" style={{ animationDelay: "0.32s" }}>
              <button
                className="nw-btn-submit"
                type="submit"
                disabled={loading || phase !== "idle"}
                style={{
                  width: "100%",
                  padding: "13px",
                  marginTop: 4,
                  background:
                    phase === "success"
                      ? `linear-gradient(135deg,${NW.success},#4DB3A5)`
                      : NW.violetDark,
                  backgroundSize: loading ? "200% 100%" : undefined,
                  animation: loading
                    ? "nwShimmer 1.4s linear infinite"
                    : undefined,
                  border: "none",
                  borderRadius: 12,
                  color: "#FFFFFF",
                  fontWeight: 800,
                  fontSize: 14,
                  fontFamily: FONTS.heading,
                  letterSpacing: 0.2,
                  cursor:
                    loading || phase !== "idle" ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                  boxShadow:
                    phase === "success"
                      ? "0 4px 20px rgba(0,160,140,0.35)"
                      : "0 4px 18px rgba(29,91,168,0.3)",
                  transition:
                    "transform 0.15s,box-shadow 0.15s,background 0.3s",
                  opacity: loading ? 0.88 : 1,
                }}
              >
                {loading ? (
                  <>
                    <NwSpinner />
                    Connexion en cours…
                  </>
                ) : phase === "success" ? (
                  <>
                    <span style={{ fontSize: 16 }}>✓</span>Connecté — Bienvenue
                    !
                  </>
                ) : (
                  <>
                    <span style={{ fontSize: 14 }}>▶</span>Se connecter
                  </>
                )}
              </button>
            </div>
          </form>

          <div
            style={{
              marginTop: 28,
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <div style={{ flex: 1, height: 1, background: NW.gray200 }} />
            <span
              style={{ fontSize: 11, color: NW.gray300, whiteSpace: "nowrap" }}
            >
              accès sécurisé
            </span>
            <div style={{ flex: 1, height: 1, background: NW.gray200 }} />
          </div>

          <div
            style={{
              textAlign: "center",
              marginTop: 22,
              fontSize: 11,
              color: NW.gray500,
              fontFamily: FONTS.body,
            }}
          >
            © {new Date().getFullYear()} Avantages  Soft · Tous droits réservés
          </div>
        </div>
      </div>

      {/* ── 3 popups mot de passe oublié ── */}
      <PopupEmail
        visible={forgotStep === "email"}
        onClose={() => setForgotStep("none")}
        onSuccess={(em) => {
          setForgotEmail(em);
          setForgotStep("otp");
        }}
      />
      <PopupOtp
        visible={forgotStep === "otp"}
        email={forgotEmail}
        onClose={() => setForgotStep("none")}
        onSuccess={() => setForgotStep("reset")}
      />
      <PopupResetPassword
        visible={forgotStep === "reset"}
        email={forgotEmail}
        onClose={() => setForgotStep("none")}
        onSuccess={() => {
          setForgotStep("none");
          setForgotEmail("");
        }}
      />
    </>
  );
};

export default AuthPage;
